import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSetQuestionVoidMutation } from '@/api/live-scoring/setQuestionVoid';
import { toast } from 'sonner';
import { Ban, Loader2, Undo2 } from 'lucide-react';

interface VoidQuestionControlProps {
  questionId: string;
  /** Current void state from the question record. */
  voided: boolean;
  voidedReason?: string | null;
}

/**
 * Void / un-void control for a question shown in live scoring.
 *
 * Voiding a question ("no contest") marks EVERY option correct, so every user who answered
 * gets the points — used when a result can't fairly be settled (e.g. a player who never took
 * part in the match). The backend re-grades and re-scores the pickem (points + payouts).
 */
export function VoidQuestionControl({
  questionId,
  voided,
  voidedReason,
}: VoidQuestionControlProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const voidMutation = useSetQuestionVoidMutation();

  useEffect(() => {
    if (confirmOpen) setReason('');
  }, [confirmOpen]);

  const applyVoid = async (nextVoided: boolean) => {
    try {
      const result = await voidMutation.mutateAsync({
        questionId,
        voided: nextVoided,
        reason: nextVoided ? reason || undefined : undefined,
      });

      const label = nextVoided ? 'Question voided' : 'Question restored';
      if (!nextVoided && result?.regraded === false) {
        // Un-void couldn't auto-grade the question (e.g. a manual/custom question with no
        // feed data), so it was reset to "no result". It must now be graded manually.
        toast.warning(
          `${label}, but it couldn't be graded automatically and now has no result. ` +
            'Grade it manually, then run Recalculate Prizes to update payouts.'
        );
      } else if (result?.scored) {
        toast.success(`${label}. Scores recalculated — run Recalculate Prizes to update payouts.`);
      } else if (result?.scoringSkippedReason) {
        toast.warning(`${label}, but scores were not recalculated: ${result.scoringSkippedReason}`);
      } else {
        toast.success(label);
      }
      setConfirmOpen(false);
    } catch (error) {
      toast.error(
        `Failed to ${nextVoided ? 'void' : 'restore'} question: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  };

  if (voided) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="destructive" className="gap-1">
                <Ban className="h-3 w-3" />
                Voided — all answers correct
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              This question is a "no contest": every option is graded correct, so everyone who
              answered earns the points.
              {voidedReason ? <div className="mt-1 italic">Reason: {voidedReason}</div> : null}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={voidMutation.isPending}
                onClick={() => applyVoid(false)}
              >
                {voidMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Undo2 className="h-4 w-4" />
                )}
                Un-void
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Remove the void and grade normally (e.g. a single Over/Under winner is restored). If
              the question can't be graded automatically, it's left with no result for you to grade
              manually. Run Recalculate Prizes afterwards to update payouts.
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" size="sm" variant="outline" onClick={() => setConfirmOpen(true)}>
            <Ban className="h-4 w-4" />
            Void question
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Mark this question "no contest": all options become correct and everyone who answered gets
          the points. Recalculates the leaderboard; run Recalculate Prizes afterwards to update
          payouts.
        </TooltipContent>
      </Tooltip>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void this question?</DialogTitle>
            <DialogDescription>
              Every option will be marked correct, so all users who answered this question will
              receive the points. The pickem's leaderboard will be recalculated — run Recalculate
              Prizes afterwards to update payouts. You can un-void later to restore normal grading.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="void-reason">Reason (optional)</Label>
            <Textarea
              id="void-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Player did not take part in the match"
              rows={3}
              maxLength={500}
            />
            <p className="text-muted-foreground text-right text-xs">{reason.length}/500</p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={voidMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => applyVoid(true)}
              disabled={voidMutation.isPending}
            >
              {voidMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recalculating scores…
                </>
              ) : (
                'Void question'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
